import type { ApolloClient } from '@apollo/client';
import {
    AttributeValueType,
    ReportBuilderAttributeRuntimeDataDocument,
    type ReportBuilderAttributeRuntimeDataQuery,
    type ReportBuilderAttributeRuntimeDataQueryVariables,
} from '@/graphql/types';
import type { BindingId } from '../types';
import { createDefaultImageSettings } from '../../../elements/image/contracts';
import { createDefaultImageGallerySettings } from '../../../elements/imageGallery/contracts';
import type {
    ElementDefinition,
    ElementInsertionPresetOption,
    ElementInsertionPresetProvider,
    RenderContext,
} from '../../../elements/types';
import { ATTRIBUTES_IMAGE_BINDING_ID } from './argOptions';
import {
    formatAttributeLabel,
    getAttributeDisplayLabels,
} from '../../../../attributes/builtInLabels';
import { resolveAttributeRuntimeQueryVariables } from './queryVariables';

type AttributePresetEntry = ReportBuilderAttributeRuntimeDataQuery['attributesByEntity'][number];

const ATTRIBUTE_TEXT_BINDING_ID = 'attributes.text' as BindingId;
const ATTRIBUTE_IMAGE_LIST_BINDING_ID = 'attributes.image_list' as BindingId;

function getAttributeValueTypeForElementType(elementType: string): AttributeValueType | null {
    if (elementType === 'text') {
        return AttributeValueType.Text;
    }

    if (elementType === 'image') {
        return AttributeValueType.Image;
    }

    if (elementType === 'image_gallery') {
        return AttributeValueType.ImageList;
    }

    return null;
}

function createAttributeTextDefinition(entry: AttributePresetEntry): ElementDefinition {
    return {
        type: 'text',
        settings: {
            text: `{{${ATTRIBUTE_TEXT_BINDING_ID}:${entry.definitionId}}}`,
        },
    };
}

function createAttributeImageDefinition(entry: AttributePresetEntry): ElementDefinition {
    return {
        type: 'image',
        settings: {
            ...createDefaultImageSettings(),
            binding: {
                bindingId: ATTRIBUTES_IMAGE_BINDING_ID,
                arg: entry.definitionId,
            },
            alt: formatAttributeLabel(entry),
        },
    };
}

function createAttributeImageGalleryDefinition(entry: AttributePresetEntry): ElementDefinition {
    return {
        type: 'image_gallery',
        settings: {
            ...createDefaultImageGallerySettings(),
            binding: {
                bindingId: ATTRIBUTE_IMAGE_LIST_BINDING_ID,
                arg: entry.definitionId,
            },
        },
    };
}

function createAttributeDefinitionForElementType(
    elementType: string,
    entry: AttributePresetEntry
): ElementDefinition {
    if (elementType === 'text') {
        return createAttributeTextDefinition(entry);
    }

    if (elementType === 'image') {
        return createAttributeImageDefinition(entry);
    }

    if (elementType === 'image_gallery') {
        return createAttributeImageGalleryDefinition(entry);
    }

    throw new Error(`Element "${elementType}" does not support attribute presets.`);
}

function getAttributePresetDescription(entry: AttributePresetEntry): string {
    if (entry.valueType === AttributeValueType.Text) {
        return gettext('ATTRIBUTE_PRESET_TEXT_DESCRIPTION');
    }

    if (entry.valueType === AttributeValueType.Image) {
        return gettext('ATTRIBUTE_PRESET_IMAGE_DESCRIPTION');
    }

    return gettext('ATTRIBUTE_PRESET_IMAGE_LIST_DESCRIPTION');
}

function createAttributePresetOption(
    elementType: string,
    entry: AttributePresetEntry,
    label: string
): ElementInsertionPresetOption {
    return {
        key: `attribute:${entry.valueType}:${entry.definitionId}`,
        label,
        description: getAttributePresetDescription(entry),
        sourceLabel: gettext('ATTRIBUTE_PRESET_SOURCE_LABEL'),
        supportedSwarmTypes: null,
        isEnabled: true,
        createDefinition: () => createAttributeDefinitionForElementType(elementType, entry),
    };
}

function elementTypeSupportsAttributePresets(elementType: string): boolean {
    return getAttributeValueTypeForElementType(elementType) !== null;
}

async function resolveAttributeInsertionPresets({
    client,
    context,
    elementType,
}: {
    client: ApolloClient<object>;
    context: RenderContext;
    elementType: string;
}): Promise<readonly ElementInsertionPresetOption[]> {
    const valueType = getAttributeValueTypeForElementType(elementType);
    if (valueType === null) {
        return [];
    }

    const { data } = await client.query<
        ReportBuilderAttributeRuntimeDataQuery,
        ReportBuilderAttributeRuntimeDataQueryVariables
    >({
        query: ReportBuilderAttributeRuntimeDataDocument,
        variables: resolveAttributeRuntimeQueryVariables(context.kind, context),
        fetchPolicy: 'cache-first',
    });

    const entries = data.attributesByEntity.filter((entry) => entry.valueType === valueType);
    const labels = getAttributeDisplayLabels(entries, formatAttributeLabel);

    return entries.map((entry, index) =>
        createAttributePresetOption(elementType, entry, labels[index])
    );
}

export const attributeInsertionPresetProvider: ElementInsertionPresetProvider = {
    supports({ elementType }) {
        return elementTypeSupportsAttributePresets(elementType);
    },
    resolvePresets: resolveAttributeInsertionPresets,
};
